import { Icon } from '@advanza/ui'
import clsx from 'clsx'
import { DISCOUNT_STATUS } from 'hooks/providerHooks'
import { PromoStatus, ProviderPromo, STATUS_PENDING } from 'interfaces/ProviderPromos'

// Ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
}

const getStatusColor = (status: PromoStatus): string => {
    if (status === STATUS_PENDING) {
        return 'text-tertiary-500'
    }
    if (status === DISCOUNT_STATUS.ACTIVE) {
        return 'text-success'
    }
    return 'text-error'
}

const formatDateWithOrdinal = (dateString: string, includeYear: boolean = true): string => {
    const date = new Date(dateString)
    const day = date.getDate()
    const month = date.toLocaleDateString('en-GB', { month: 'long' })
    const year = date.getFullYear()
    const ordinalDay = getOrdinal(day)

    if (includeYear) {
        return `${ordinalDay} of ${month} ${year}`
    }
    return `${ordinalDay} of ${month}`
}

interface OfferData {
    value?: number | string
    type?: string
    promo_length?: number | null
    expiryDate?: string
    endDate?: string
    prices?: Array<Record<string, unknown>>
}

const formatValueDisplay = (value: number | string, type: string): string => {
    if (type === 'percentage') return `${value}%`
    if (type === 'amount') return `€${value}`
    return String(value)
}

const formatPromoLength = (months: number): string => `${months} month${months === 1 ? '' : 's'}`

const OFFER_FORMATTERS: Record<string, (data: OfferData) => string> = {
    'subscription-discount': ({ value = '', type = '', promo_length }) => {
        const display = formatValueDisplay(value, type)
        if (promo_length !== null && promo_length !== undefined) {
            return promo_length === 0
                ? `${display} discount (one-time)`
                : `${display} discount for ${formatPromoLength(promo_length)}`
        }
        return `${display} discount (ongoing)`
    },
    'subscription-discount-fixed-date': ({ value = '', type = '', endDate }) => {
        const display = formatValueDisplay(value, type)
        if (endDate) {
            return `${display} discount until ${formatDateWithOrdinal(endDate)}`
        }
        return `${display} discount (no end date)`
    },
    'one-time-leads': ({ value = '', expiryDate }) => {
        const display = String(value)
        if (expiryDate) {
            return `${display} one-time leads (expires: ${new Date(expiryDate).toLocaleDateString()})`
        }
        return `${display} one-time leads`
    },
    'recurring-leads': ({ value = '', promo_length }) => {
        const display = String(value)
        if (promo_length !== null && promo_length !== undefined) {
            return `${display} recurring leads for ${formatPromoLength(promo_length)}`
        }
        return `${display} recurring leads (ongoing)`
    },
    'subscription-overwrite-price': ({ value = '' }) => `Subscription override price: €${value}/mo`,
    'custom-lead-prices': ({ prices }) =>
        Array.isArray(prices) && prices.length > 0
            ? `${prices.length} custom lead price${prices.length === 1 ? '' : 's'}`
            : '',
}

const renderOffersPreview = (offersInfo: ProviderPromo['offers_info']): string | null => {
    if (!Array.isArray(offersInfo) || offersInfo.length === 0) {
        return null
    }

    const formattedOffers: string[] = []

    for (const offerObj of offersInfo) {
        if (typeof offerObj !== 'object' || offerObj === null) {
            continue
        }
        for (const [offerType, offerData] of Object.entries(offerObj)) {
            if (typeof offerData !== 'object' || offerData === null) {
                continue
            }
            const formatter = OFFER_FORMATTERS[offerType]
            if (!formatter) {
                continue
            }
            const text = formatter(offerData as OfferData)
            if (text) {
                formattedOffers.push(text)
            }
        }
    }

    return formattedOffers.length > 0 ? formattedOffers.join(', ') : null
}

interface ProviderPromosListProps {
    promos: ProviderPromo[]
    onDeletePromo: (promoId: number) => void
}

const ProviderPromosList = ({ promos, onDeletePromo }: ProviderPromosListProps) => {
    const getTypeSourceText = (promo: ProviderPromo) => {
        const typeText = `type: ${promo.type || 'N/A'}`
        const userName = (() => {
            if (promo.source !== 'office' || !promo.user) return ''
            const { first_name, last_name, username } = promo.user
            if (first_name && last_name) return ` by ${first_name} ${last_name}`
            if (first_name || last_name) return ` by ${first_name || last_name}`
            if (username) return ` by ${username}`
            return ''
        })()
        const sourceText = `source: ${promo.source || 'N/A'}${userName}`
        return `(${typeText} | ${sourceText})`
    }

    if (!promos || promos.length === 0) {
        return (
            <div>
                <div className="text-md text-neutral-500">No promos available</div>
            </div>
        )
    }

    return (
        <div>
            <div>
                {promos
                    .sort((a, b) => {
                        // Sort by start_date, with null values at the end
                        if (!a.start_date && !b.start_date) return 0
                        if (!a.start_date) return 1
                        if (!b.start_date) return -1
                        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
                    })
                    .map((promo) => {
                        const isPendingPromo = promo.status === STATUS_PENDING
                        const isInactive = promo.status === DISCOUNT_STATUS.INACTIVE
                        const offersPreview = renderOffersPreview(promo.offers_info)
                        const typeSourceText = getTypeSourceText(promo)

                        // Fall back to the marketing promo's expiry date when the
                        // service-provider promo doesn't have its own end date, so
                        // pending promos created without an end_date still show a
                        // valid-until indicator.
                        const effectiveEndDate = promo.end_date ?? promo.promo_expiry_date ?? null

                        // Check the service provider promo's end_date, not the promo entity's expiry_date
                        const isExpired = promo.end_date
                            ? new Date(promo.end_date) < new Date()
                            : false

                        // Expired promos should be treated like inactive ones visually
                        const shouldShowAsInactive = isInactive || isExpired

                        const deleteIcon = (promo.status === DISCOUNT_STATUS.ACTIVE ||
                            (isPendingPromo && !isExpired)) && (
                            <Icon
                                name="delete"
                                classNameIcon="hover:text-error cursor-pointer"
                                onClick={() => onDeletePromo(promo.service_provider_promo_id)}
                            />
                        )

                        return (
                            <div
                                key={promo.service_provider_promo_id}
                                className="py-2 border-b last:border-b-0">
                                {/* First row: offers_info preview + (type: ... | source: ...) */}
                                <div
                                    className={clsx(
                                        'text-md mb-1',
                                        shouldShowAsInactive && 'line-through',
                                    )}>
                                    {offersPreview ? (
                                        <span>
                                            <span className="font-bold">{offersPreview}</span>{' '}
                                            <span className="text-neutral-600">
                                                {typeSourceText}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-neutral-600">{typeSourceText}</span>
                                    )}
                                    {' - '}
                                    <span
                                        className={clsx('font-bold', getStatusColor(promo.status))}>
                                        {promo.status.charAt(0).toUpperCase() +
                                            promo.status.slice(1)}
                                    </span>
                                </div>
                                {/* Dates row */}
                                {(promo.start_date || effectiveEndDate) && (
                                    <div className="flex items-center justify-between text-neutral-500 text-md mt-1">
                                        <div
                                            className={clsx(
                                                shouldShowAsInactive && 'line-through',
                                            )}>
                                            {promo.start_date && effectiveEndDate && (
                                                <>
                                                    From{' '}
                                                    <span className="underline">
                                                        {formatDateWithOrdinal(promo.start_date)}
                                                    </span>{' '}
                                                    until{' '}
                                                    <span className="underline">
                                                        {formatDateWithOrdinal(
                                                            effectiveEndDate,
                                                            new Date(
                                                                promo.start_date,
                                                            ).getFullYear() !==
                                                                new Date(
                                                                    effectiveEndDate,
                                                                ).getFullYear(),
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                            {promo.start_date && !effectiveEndDate && (
                                                <>
                                                    From{' '}
                                                    <span className="underline">
                                                        {formatDateWithOrdinal(promo.start_date)}
                                                    </span>
                                                </>
                                            )}
                                            {!promo.start_date && effectiveEndDate && (
                                                <>
                                                    Until{' '}
                                                    <span className="underline">
                                                        {formatDateWithOrdinal(effectiveEndDate)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">{deleteIcon}</div>
                                    </div>
                                )}
                                {/* Buttons row (for promos without dates) */}
                                {!promo.start_date && !effectiveEndDate && (
                                    <div className="flex items-center justify-end mt-1">
                                        <div className="flex items-center gap-2">{deleteIcon}</div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}

export default ProviderPromosList
